import React, { useState, useEffect, useRef } from 'react';
import {
  Box, Typography, Stack, Button, IconButton, Dialog, DialogContent,
  DialogTitle, DialogActions, DialogContentText, Grid, TextField, MenuItem,
  Alert, Paper, InputAdornment, Tooltip, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Chip, Avatar
} from '@mui/material';
import {
  NoteAdd, Assignment, LocalHospital, EventNote, MedicalServices,
  Psychology, Healing, EventRepeat, CheckCircle, Edit, Delete,
  PictureAsPdf, Visibility, LocalOffer, Person, Schedule, Face, WarningAmber, Close
} from '@mui/icons-material';

const ReservationReportsTab = ({ reservation, user, apiUrl, token: propToken }) => {
  const data = reservation?.originalData;
  const statusKey = data?.status_reservation;
  const token = propToken || localStorage.getItem('token');
  const rolePath = (user && Number(user.role_user || user.role) === 4) ? 'praticien' : 'admin';
 console.log('reservation', data )
  // Référence pour le défilement automatique dans la modale d'édition
  const formTopRef = useRef(null);

  const [openReportDialog, setOpenReportDialog] = useState(false);
  const [openViewDialog, setOpenViewDialog] = useState(false);
  const [selectedReportToView, setSelectedReportToView] = useState(null);

  // État pour la modale de confirmation de suppression personnalisée
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [reportToDeleteId, setReportToDeleteId] = useState(null);

  const [editingReportId, setEditingReportId] = useState(null);
  const [reportSubmitting, setReportSubmitting] = useState(false);
  const [reportError, setReportError] = useState(null);
  const [reportSuccess, setReportSuccess] = useState(null);

  const [comptesRendus, setComptesRendus] = useState([]);
  const [loadingReports, setLoadingReports] = useState(false);

  const initialFormState = {
    numero_seance: data?.numero_seance || 'Séance n°1',
    pratique: data?.offre?.info_offre?.titre_offre || data?.offre?.info_offre?.titre || '',
    date_seance: data?.date_seance ? data.date_seance.replace(' ', 'T').slice(0, 16) : new Date().toISOString().slice(0, 16),
    perception_patient: '',
    resume_seance: '',
    prevision_suite: '',
    statut: 'valide',
  };

  const [reportForm, setReportForm] = useState(initialFormState);

  const fetchComptesRendus = async () => {
    if (!data?.id_offre_reservation) return;
    setLoadingReports(true);
    try {
      const endpoint = `${apiUrl.replace(/\/+$/, '')}/${rolePath}/comptes-rendus/g/all?id_offre_reservation=${data.id_offre_reservation}`;
      const response = await fetch(endpoint, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
        },
      });

      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        throw new Error("L'API n'a pas renvoyé du JSON valide.");
      }

      const result = await response.json();
      if (response.ok) {
        const allReports = Array.isArray(result) ? result : result.data || [];
        const filtered = allReports.filter(r => String(r.id_offre_reservation) === String(data.id_offre_reservation));
        setComptesRendus(filtered);
      }
    } catch (err) {
      console.error('Erreur chargement comptes rendus:', err);
    } finally {
      setLoadingReports(false);
    }
  };

  useEffect(() => {
    if (reservation && token) {
      fetchComptesRendus();
    }
  }, [reservation, token]);

  // Fonction pour scroller automatiquement vers le haut de la modale lors d'un message
  useEffect(() => {
    if ((reportSuccess || reportError) && formTopRef.current) {
      formTopRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [reportSuccess, reportError]);

  const handleReportChange = (e) => {
    const { name, value } = e.target;
    setReportForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleOpenCreate = () => {
    setEditingReportId(null);
    setReportForm(initialFormState);
    setReportError(null);
    setReportSuccess(null);
    setOpenReportDialog(true);
  };

  const handleOpenEdit = (report) => {
    const reportId = report.id_comptes_rendus || report.id_compte_rendu || report.id;
    setEditingReportId(reportId);
    
    setReportForm({
      numero_seance: report.numero_seance || 'Séance n°1',
      pratique: report.pratique || '',
      date_seance: report.date_seance ? report.date_seance.replace(' ', 'T').slice(0, 16) : new Date().toISOString().slice(0, 16),
      perception_patient: report.perception_patient || '',
      resume_seance: report.resume_seance || '',
      prevision_suite: report.prevision_suite || '',
      statut: report.statut || 'valide',
    });
    setReportError(null);
    setReportSuccess(null);
    setOpenReportDialog(true);
  };

  const handleOpenView = (report) => {
    setSelectedReportToView(report);
    setOpenViewDialog(true);
  };

  const handleSaveReport = async () => {
    setReportSubmitting(true);
    setReportError(null);
    setReportSuccess(null);

    try {
      const isEditing = Boolean(editingReportId);
      const endpoint = isEditing
        ? `${apiUrl.replace(/\/+$/, '')}/${rolePath}/comptes-rendus/${editingReportId}`
        : `${apiUrl.replace(/\/+$/, '')}/${rolePath}/comptes-rendus`;

      const method = isEditing ? 'PUT' : 'POST';

      const response = await fetch(endpoint, {
        method: method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
        },
        body: JSON.stringify({
          id_offre_reservation: data?.id_offre_reservation,
          id_patient_profile: data?.client?.id_patient_profile || data?.id_patient_profile || 1,
          ...reportForm,
        }),
      });

      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        throw new Error("Erreur serveur : L'API a renvoyé du HTML au lieu du JSON.");
      }

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || 'Erreur lors de l’enregistrement du compte rendu.');
      }

      setReportSuccess(isEditing ? 'Compte rendu mis à jour avec succès !' : 'Compte rendu enregistré avec succès !');
      fetchComptesRendus(); 
      setTimeout(() => {
        setOpenReportDialog(false);
        setReportSuccess(null);
      }, 1500);
    } catch (err) {
      setReportError(err.message);
    } finally {
      setReportSubmitting(false);
    }
  };

  // Ouverture de la modale de confirmation de suppression
  const handleConfirmDelete = (reportId) => {
    if (!reportId) {
      alert("Erreur : ID du compte rendu introuvable.");
      return;
    }
    setReportToDeleteId(reportId);
    setDeleteConfirmOpen(true);
  };

  // Exécution effective de la suppression après confirmation
  const handleDeleteReport = async () => {
    if (!reportToDeleteId) return;

    try {
      const endpoint = `${apiUrl.replace(/\/+$/, '')}/${rolePath}/comptes-rendus/${reportToDeleteId}`;
      const response = await fetch(endpoint, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
        },
      });

      if (!response.ok) {
        const result = await response.json();
        throw new Error(result.message || 'Erreur lors de la suppression.');
      }

      setDeleteConfirmOpen(false);
      setReportToDeleteId(null);
      fetchComptesRendus();
    } catch (err) {
      alert("Erreur : " + err.message);
    }
  };

  const handleDownloadPDF = (report) => {
    const printWindow = window.open('', '_blank');
    
    const infoOffre = report.reservation?.offre?.info_offre || {};
    const offreTitre = infoOffre.titre_offre || report.pratique || 'Prestation SOA';
    const offreLieu = infoOffre.address_ws ? `${infoOffre.address_ws}, ${infoOffre.city_ws}` : 'Locaux S.O.A';
    
    const praticien = report.user || {};
    const praticienNom = [praticien.prenom_user, praticien.nom_user].filter(Boolean).join(' ') || 'Praticien SOA';

    const htmlContent = `
      <!DOCTYPE html>
      <html lang="fr">
      <head>
        <meta charset="UTF-8">
        <title>Compte Rendu - ${report.numero_seance || 'Séance'}</title>
        <style>
          body { font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif; color: #1E293B; line-height: 1.6; padding: 40px; background-color: #FFFFFF; }
          .header { border-bottom: 3px solid #3311CC; padding-bottom: 20px; margin-bottom: 25px; display: flex; justify-content: space-between; align-items: flex-start; }
          .logo-area { font-size: 20px; font-weight: 800; color: #3311CC; text-transform: uppercase; letter-spacing: 1px; }
          .title { font-size: 22px; font-weight: bold; color: #0F172A; margin: 5px 0 0 0; }
          .subtitle { font-size: 14px; color: #64748B; margin-top: 5px; }
          .badge { background: #E0F2FE; color: #0284C7; padding: 6px 14px; border-radius: 20px; font-size: 12px; font-weight: bold; text-transform: uppercase; display: inline-block; }
          .info-box { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; background: #F8FAFC; border: 1px solid #E2E8F0; border-radius: 12px; padding: 20px; margin-bottom: 25px; }
          .info-item { font-size: 13px; color: #475569; margin-bottom: 6px; }
          .info-item strong { color: #1E293B; }
          .section { background: #FFFFFF; border: 1px solid #E2E8F0; border-left: 4px solid #0EA5E9; border-radius: 8px; padding: 18px; margin-bottom: 18px; }
          .section.green { border-left-color: #10B981; }
          .section.indigo { border-left-color: #6366F1; }
          .section-title { font-size: 13px; font-weight: bold; text-transform: uppercase; color: #0EA5E9; margin-bottom: 8px; }
          .section.green .section-title { color: #10B981; }
          .section.indigo .section-title { color: #6366F1; }
          .section-text { font-size: 14px; color: #334155; white-space: pre-line; }
          .footer { margin-top: 40px; text-align: center; font-size: 11px; color: #94A3B8; border-top: 1px solid #E2E8F0; padding-top: 15px; }
          @media print { body { padding: 20px; } .section { break-inside: avoid; } }
        </style>
      </head>
      <body>
        <div class="header">
          <div>
            <div class="logo-area">Plateforme S.O.A</div>
            <div class="title">Compte Rendu & Suivi</div>
            <div class="subtitle">Prestation : <strong>${offreTitre}</strong></div>
          </div>
          <div>
            <span class="badge">${report.statut === 'valide' ? 'Validé & Signé' : 'Brouillon'}</span>
          </div>
        </div>

        <div class="info-box">
          <div>
            <div class="info-item"><strong>Séance :</strong> ${report.numero_seance || 'Séance n°1'}</div>
            <div class="info-item"><strong>Date de consultation :</strong> ${report.date_seance || '—'}</div>
            <div class="info-item"><strong>Lieu :</strong> ${offreLieu}</div>
          </div>
          <div>
            <div class="info-item"><strong>Intervenant / Praticien :</strong> ${praticienNom}</div>
            <div class="info-item"><strong>Date de rédaction :</strong> ${report.created_at ? new Date(report.created_at).toLocaleString('fr-FR') : '—'}</div>
          </div>
        </div>

        ${report.pratique ? `<div class="section"><div class="section-title">Pratique & Approche</div><div class="section-text">${report.pratique}</div></div>` : ''}
        ${report.perception_patient ? `<div class="section"><div class="section-title">Perception & État du Patient</div><div class="section-text">${report.perception_patient}</div></div>` : ''}
        ${report.resume_seance ? `<div class="section green"><div class="section-title">Résumé & Actes Réalisés</div><div class="section-text">${report.resume_seance}</div></div>` : ''}
        ${report.prevision_suite ? `<div class="section indigo"><div class="section-title">Plan de Soins & Suite</div><div class="section-text">${report.prevision_suite}</div></div>` : ''}

        <div class="footer">
          Plateforme S.O.A (Soins d'Onco-Accompagnement) • Document officiel généré le ${new Date().toLocaleDateString('fr-FR')}
        </div>

        <script>
          window.onload = function() { window.print(); window.onafterprint = function() { window.close(); } }
        </script>
      </body>
      </html>
    `;

    printWindow.document.write(htmlContent);
    printWindow.document.close();
  };

  const isTerminated = 
    ['termine', 'terminée', 'completed'].includes((statusKey || '').toLowerCase()) || 
    statusKey?.toLowerCase().includes('termin');

  const canAddReport = isTerminated; 

  // --- Calculs des comptes rendus traités vs non traités pour cette réservation ---
  let traitesCount = 0;
  let nonTraitesCount = 0;

  if (comptesRendus.length === 0) {
    nonTraitesCount = 1; 
  } else {
    comptesRendus.forEach((cr) => {
      const st = (cr.statut || '').toLowerCase();
      if (st === 'valide' || st === 'validé') {
        traitesCount += 1;
      } else {
        nonTraitesCount += 1;
      }
    });
  }

  return (
    <Stack spacing={3}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, color: '#1E293B', display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <MedicalServices color="primary" /> Dossier des Comptes Rendus {comptesRendus.length > 0 ? `(${comptesRendus.length})` : ''}
        </Typography>

        {/* --- Badges État Comptes Rendus (Traités vs À traiter) --- */}
        <Stack direction="row" spacing={1} alignItems="center">
          <Tooltip title="Comptes rendus validés / traités">
            <Chip
              label={`Traités : ${traitesCount}`}
              size="small"
              sx={{
                fontWeight: 700,
                backgroundColor: '#dcfce7',
                color: '#15803d',
                borderRadius: '8px',
                border: '1px solid #86efac'
              }}
            />
          </Tooltip>

          <Tooltip title="Brouillons ou absents">
            <Chip
              icon={nonTraitesCount > 0 ? <WarningAmber sx={{ color: '#dc2626 !important', fontSize: '16px' }} /> : undefined}
              label={`À traiter : ${nonTraitesCount}`}
              size="small"
              sx={{
                fontWeight: 700,
                backgroundColor: nonTraitesCount > 0 ? '#fee2e2' : '#f1f5f9',
                color: nonTraitesCount > 0 ? '#dc2626' : '#64748b',
                borderRadius: '8px',
                border: `1px solid ${nonTraitesCount > 0 ? '#fca5a5' : '#cbd5e1'}`
              }}
            />
          </Tooltip>

          {canAddReport && (
            <Button
              variant="contained"
              startIcon={<NoteAdd />}
              onClick={handleOpenCreate}
              sx={{
                bgcolor: '#0EA5E9',
                borderRadius: '10px',
                textTransform: 'none',
                fontWeight: 700,
                px: 2.5,
                boxShadow: '0 4px 12px rgba(14, 165, 233, 0.2)',
                ml: 1,
                '&:hover': { bgcolor: '#0284C7' }
              }}
            >
              Rédiger
            </Button>
          )}
        </Stack>
      </Box>

      {!isTerminated && (
        <Alert severity="warning" sx={{ borderRadius: '10px' }}>
          Le statut actuel de la séance est <strong>« {statusKey || 'en cours'} »</strong>. Veuillez le passer à <strong>« Terminé »</strong> pour rédiger le compte rendu.
        </Alert>
      )}

      {loadingReports ? (
        <Typography sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
          Chargement des dossiers...
        </Typography>
      ) : comptesRendus.length === 0 ? (
        <Paper
          elevation={0}
          sx={{
            p: 6,
            textAlign: 'center',
            borderRadius: 4,
            border: '2px dashed #E2E8F0',
            bgcolor: '#FFFFFF',
          }}
        >
          <Assignment sx={{ fontSize: 56, color: '#94A3B8', mb: 2 }} />
          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#334155', mb: 1 }}>
            Aucun compte rendu rédigé
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: '450px', mx: 'auto' }}>
            {canAddReport 
              ? 'Cliquez ci-dessous pour consigner les observations et le suivi de cette séance.' 
              : 'Les comptes rendus seront disponibles ici une fois la séance clôturée.'}
          </Typography>

          {canAddReport && (
            <Button
              variant="contained"
              startIcon={<NoteAdd />}
              onClick={handleOpenCreate}
              sx={{
                bgcolor: '#3311CC',
                borderRadius: '10px',
                textTransform: 'none',
                fontWeight: 700,
                px: 4,
                py: 1.2,
                boxShadow: '0 4px 14px rgba(51, 17, 204, 0.3)',
                '&:hover': { bgcolor: '#220899' },
              }}
            >
              Rédiger le compte rendu
            </Button>
          )}
        </Paper>
      ) : (
        <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 4, border: '1px solid #E2E8F0', overflow: 'hidden' }}>
          <Table sx={{ minWidth: 650 }}>
            <TableHead sx={{ bgcolor: '#F8FAFC' }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 800, color: '#475569' }}>Compte Rendu & Prestation</TableCell>
                <TableCell sx={{ fontWeight: 800, color: '#475569' }}>Statut Document</TableCell>
                <TableCell sx={{ fontWeight: 800, color: '#475569' }}>Date de Séance</TableCell>
                <TableCell sx={{ fontWeight: 800, color: '#475569' }}>Date de Création</TableCell>
                <TableCell align="right" sx={{ fontWeight: 800, color: '#475569', pr: 3 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {comptesRendus.map((report, index) => {
                const reportId = report.id_comptes_rendus || report.id_compte_rendu || report.id;
                const isValidated = (report.statut || '').toLowerCase() === 'valide' || (report.statut || '').toLowerCase() === 'validé';
                const offreTitre = report.reservation?.offre?.info_offre?.titre_offre || report.pratique || 'Prestation';
                
                return (
                  <TableRow key={reportId || index} sx={{ '&:hover': { bgcolor: '#F8FAFC' } }}>
                    <TableCell>
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <LocalHospital color="primary" fontSize="small" />
                        <Box>
                          <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#1E293B' }}>
                            {report.numero_seance || `Séance #${index + 1}`}
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#0EA5E9', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <LocalOffer sx={{ fontSize: '12px' }} /> {offreTitre}
                          </Typography>
                        </Box>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={isValidated ? 'Validé et Signé' : 'Brouillon'}
                        size="small"
                        sx={{
                          bgcolor: isValidated ? '#D1FAE5' : '#FEF3C7',
                          color: isValidated ? '#059669' : '#D97706',
                          fontWeight: 700,
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ color: '#334155', fontWeight: 600 }}>
                        {report.date_seance ? new Date(report.date_seance).toLocaleDateString('fr-FR') : '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ color: '#64748B' }}>
                        {report.created_at ? new Date(report.created_at).toLocaleString('fr-FR') : '—'}
                      </Typography>
                    </TableCell>
                    <TableCell align="right" sx={{ pr: 2 }}>
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                        <Tooltip title="Voir les détails">
                          <IconButton size="small" onClick={() => handleOpenView(report)} sx={{ color: '#6366F1', bgcolor: '#EEF2FF' }}>
                            <Visibility fontSize="small" />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title="Télécharger en PDF">
                          <IconButton size="small" onClick={() => handleDownloadPDF(report)} sx={{ color: '#10B981', bgcolor: '#D1FAE5' }}>
                            <PictureAsPdf fontSize="small" />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title="Modifier">
                          <IconButton size="small" onClick={() => handleOpenEdit(report)} sx={{ color: '#0EA5E9', bgcolor: '#E0F2FE' }}>
                            <Edit fontSize="small" />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title="Supprimer">
                          <IconButton size="small" onClick={() => handleConfirmDelete(reportId)} sx={{ color: '#EF4444', bgcolor: '#FEE2E2' }}>
                            <Delete fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* --- MODALE DE CONFIRMATION DE SUPPRESSION --- */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        PaperProps={{ sx: { borderRadius: '16px', p: 1, maxWidth: '400px' } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, color: '#EF4444', fontWeight: 800 }}>
          <WarningAmber /> Confirmation de suppression
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: '#334155', fontSize: '14px' }}>
            Êtes-vous sûr de vouloir supprimer ce compte rendu ? Cette action est irréversible.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0 }}>
          <Button onClick={() => setDeleteConfirmOpen(false)} color="inherit" sx={{ fontWeight: 600, textTransform: 'none' }}>
            Annuler
          </Button>
          <Button
            variant="contained"
            onClick={handleDeleteReport}
            sx={{
              bgcolor: '#EF4444',
              borderRadius: '10px',
              textTransform: 'none',
              fontWeight: 700,
              boxShadow: '0 4px 12px rgba(239, 68, 68, 0.3)',
              '&:hover': { bgcolor: '#DC2626' },
            }}
          >
            Supprimer
          </Button>
        </DialogActions>
      </Dialog>

      {/* --- MODALE DE VISUALISATION (Design identique à l'image demandée) --- */}
      <Dialog 
        open={openViewDialog} 
        onClose={() => setOpenViewDialog(false)} 
        fullWidth 
        maxWidth="md"
        PaperProps={{ sx: { borderRadius: '20px', p: 0, overflow: 'hidden' } }}
      >
        <DialogTitle sx={{ 
          bgcolor: '#FFFFFF', 
          borderBottom: '1px solid #E2E8F0', 
          py: 2.5, 
          px: 3, 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center' 
        }}>
          <Stack direction="row" spacing={2} alignItems="center">
            <Avatar sx={{ bgcolor: '#3311CC', width: 42, height: 42 }}>
              <Assignment sx={{ color: '#FFFFFF' }} />
            </Avatar>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 800, color: '#0F172A', lineHeight: 1.2 }}>
                Compte Rendu Médical
              </Typography>
              <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 600 }}>
                {selectedReportToView?.numero_seance || 'Séance'} {selectedReportToView?.id ? `• Réf #${selectedReportToView.id}` : ''}
              </Typography>
            </Box>
          </Stack>

          <Stack direction="row" spacing={1.5} alignItems="center">
            <Chip
              label={((selectedReportToView?.statut || '').toLowerCase() === 'valide' || (selectedReportToView?.statut || '').toLowerCase() === 'validé') ? 'Validé' : 'Brouillon'}
              size="small"
              sx={{
                bgcolor: ((selectedReportToView?.statut || '').toLowerCase() === 'valide' || (selectedReportToView?.statut || '').toLowerCase() === 'validé') ? '#DCFCE7' : '#FFEDD5',
                color: ((selectedReportToView?.statut || '').toLowerCase() === 'valide' || (selectedReportToView?.statut || '').toLowerCase() === 'validé') ? '#15803D' : '#C2410C',
                fontWeight: 700,
                borderRadius: '8px',
                px: 1
              }}
            />
            <IconButton onClick={() => setOpenViewDialog(false)} size="small" sx={{ color: '#94A3B8' }}>
              <Close />
            </IconButton>
          </Stack>
        </DialogTitle>

        <DialogContent sx={{ p: 3, bgcolor: '#FFFFFF' }}>
          {selectedReportToView && (
            <Stack spacing={2.5}>
              {/* En-tête Patient / Praticien */}
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Paper elevation={0} sx={{ p: 2, borderRadius: 3, bgcolor: '#F8FAFC', border: '1px solid #E2E8F0', height: '100%', display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Avatar sx={{ bgcolor: '#E0E7FF', color: '#4338CA', width: 40, height: 40 }}>
                      <Person />
                    </Avatar>
                    <Box>
                      <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        Patient / Client
                      </Typography>
                      <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#1E293B' }}>
                        {data?.client?.prenom && data?.client?.nom ? `${data.client.prenom} ${data.client.nom}` : (data?.client_nom || 'Client S.O.A')}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#64748B' }}>
                        {data?.client?.email || '—'}
                      </Typography>
                    </Box>
                  </Paper>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Paper elevation={0} sx={{ p: 2, borderRadius: 3, bgcolor: '#F8FAFC', border: '1px solid #E2E8F0', height: '100%', display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Avatar sx={{ bgcolor: '#DCFCE7', color: '#15803D', width: 40, height: 40 }}>
                      <LocalHospital />
                    </Avatar>
                    <Box>
                      <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        Praticien
                      </Typography>
                      <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#1E293B' }}>
                        {selectedReportToView.user ? `${selectedReportToView.user.prenom_user} ${selectedReportToView.user.nom_user}` : (user ? `${user.prenom_user || user.prenom || ''} ${user.nom_user || user.nom || ''}` : 'Praticien S.O.A')}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#64748B' }}>
                        Soin : {selectedReportToView.pratique || data?.offre?.info_offre?.titre || '—'}
                      </Typography>
                    </Box>
                  </Paper>
                </Grid>
              </Grid>

              {/* Date de séance */}
              <Stack direction="row" spacing={1} alignItems="center" sx={{ color: '#475569', px: 0.5 }}>
                <EventNote color="action" fontSize="small" />
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Date séance : {selectedReportToView.date_seance ? new Date(selectedReportToView.date_seance).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' }) : '—'}
                </Typography>
              </Stack>

              {/* Blocs de contenu stylisés */}
              {selectedReportToView.perception_patient && (
                <Box sx={{ p: 2.5, bgcolor: '#F8FAFC', borderRadius: 3, border: '1px solid #E2E8F0', borderLeft: '4px solid #3311CC' }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#1E293B', mb: 1 }}>
                    Perception du patient
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#334155', whiteSpace: 'pre-line', lineHeight: 1.6 }}>
                    {selectedReportToView.perception_patient}
                  </Typography>
                </Box>
              )}

              {selectedReportToView.resume_seance && (
                <Box sx={{ p: 2.5, bgcolor: '#F8FAFC', borderRadius: 3, border: '1px solid #E2E8F0', borderLeft: '4px solid #0EA5E9' }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#1E293B', mb: 1 }}>
                    Résumé de la séance
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#334155', whiteSpace: 'pre-line', lineHeight: 1.6 }}>
                    {selectedReportToView.resume_seance}
                  </Typography>
                </Box>
              )}

              {selectedReportToView.prevision_suite && (
                <Box sx={{ p: 2.5, bgcolor: '#F8FAFC', borderRadius: 3, border: '1px solid #E2E8F0', borderLeft: '4px solid #10B981' }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#1E293B', mb: 1 }}>
                    Prévision pour la suite
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#334155', whiteSpace: 'pre-line', lineHeight: 1.6 }}>
                    {selectedReportToView.prevision_suite}
                  </Typography>
                </Box>
              )}
            </Stack>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2.5, bgcolor: '#FFFFFF', borderTop: '1px solid #E2E8F0', display: 'flex', justifyContent: 'space-between' }}>
          <Button
            variant="outlined"
            startIcon={<PictureAsPdf />}
            onClick={() => handleDownloadPDF(selectedReportToView)}
            sx={{
              borderColor: '#EF4444',
              color: '#EF4444',
              borderRadius: '10px',
              textTransform: 'none',
              fontWeight: 700,
              '&:hover': { borderColor: '#DC2626', bgcolor: '#FEF2F2' }
            }}
          >
            Télécharger / Imprimer PDF
          </Button>

          <Button
            variant="contained"
            onClick={() => setOpenViewDialog(false)}
            sx={{
              bgcolor: '#0F172A',
              borderRadius: '10px',
              textTransform: 'none',
              fontWeight: 700,
              px: 4,
              '&:hover': { bgcolor: '#1E293B' }
            }}
          >
            Fermer
          </Button>
        </DialogActions>
      </Dialog>

      {/* --- MODALE D'EDITION / CREATION --- */}
      <Dialog 
        open={openReportDialog} 
        onClose={() => setOpenReportDialog(false)} 
        fullWidth 
        maxWidth="lg"
        PaperProps={{ sx: { borderRadius: '20px', overflow: 'hidden' } }}
      >
        <DialogTitle sx={{ fontWeight: 800, bgcolor: '#3311CC', color: 'white', py: 2.5, px: 3, display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <MedicalServices /> {editingReportId ? "Modifier le Compte Rendu" : "Rédaction du Compte Rendu"}
        </DialogTitle>
        
        <DialogContent sx={{ p: 4, pt: '32px !important', bgcolor: '#F8FAFC' }}>
          <div ref={formTopRef} />

          {reportError && <Alert severity="error" sx={{ mb: 3, borderRadius: '10px' }}>{reportError}</Alert>}
          {reportSuccess && <Alert severity="success" sx={{ mb: 3, borderRadius: '10px' }}>{reportSuccess}</Alert>}

          <Stack spacing={3}>
            <Grid container spacing={2.5}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Numéro / Intitulé de la séance"
                  name="numero_seance"
                  value={reportForm.numero_seance}
                  onChange={handleReportChange}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  InputProps={{
                    sx: { borderRadius: '12px', bgcolor: 'white' },
                    startAdornment: (
                      <InputAdornment position="start">
                        <EventNote color="action" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Date et heure de la consultation"
                  name="date_seance"
                  type="datetime-local"
                  value={reportForm.date_seance}
                  onChange={handleReportChange}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  InputProps={{ sx: { borderRadius: '12px', bgcolor: 'white' } }}
                />
              </Grid>
            </Grid>

            <TextField
              label="Spécialité / Pratique effectuée"
              name="pratique"
              value={reportForm.pratique}
              onChange={handleReportChange}
              fullWidth
              placeholder="Ex: Coaching, Soin individuel..."
              InputLabelProps={{ shrink: true }}
              InputProps={{ sx: { borderRadius: '12px', bgcolor: 'white' } }}
            />

            <TextField
              label="Perception et ressentis du patient"
              name="perception_patient"
              multiline
              rows={3}
              value={reportForm.perception_patient}
              onChange={handleReportChange}
              placeholder="Ex: Patient réceptif, amélioration de la mobilité signalée, moral stable..."
              fullWidth
              InputLabelProps={{ shrink: true }}
              InputProps={{ sx: { borderRadius: '12px', bgcolor: 'white' } }}
            />

            <TextField
              label="Résumé détaillé de la séance & Actes réalisés"
              name="resume_seance"
              multiline
              rows={4}
              value={reportForm.resume_seance}
              onChange={handleReportChange}
              placeholder="Ex: Réalisation d'exercices de rééducation articulaire, conseils posturaux..."
              fullWidth
              InputLabelProps={{ shrink: true }}
              InputProps={{ sx: { borderRadius: '12px', bgcolor: 'white' } }}
            />

            <TextField
              label="Prévisions thérapeutiques et suivi ultérieur"
              name="prevision_suite"
              multiline
              rows={3}
              value={reportForm.prevision_suite}
              onChange={handleReportChange}
              placeholder="Ex: Poursuivre les exercices à domicile, prochaine séance prévue dans 15 jours..."
              fullWidth
              InputLabelProps={{ shrink: true }}
              InputProps={{ sx: { borderRadius: '12px', bgcolor: 'white' } }}
            />

            <TextField
              select
              label="Statut du rapport"
              name="statut"
              value={reportForm.statut}
              onChange={handleReportChange}
              fullWidth
              InputLabelProps={{ shrink: true }}
              InputProps={{ sx: { borderRadius: '12px', bgcolor: 'white' } }}
            >
              <MenuItem value="brouillon">Brouillon (En cours de rédaction)</MenuItem>
              <MenuItem value="valide">Validé et Signé</MenuItem>
            </TextField>

            <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ pt: 2 }}>
              <Button onClick={() => setOpenReportDialog(false)} color="inherit" sx={{ textTransform: 'none', fontWeight: 600 }}>
                Annuler
              </Button>
              <Button
                variant="contained"
                onClick={handleSaveReport}
                disabled={reportSubmitting}
                sx={{ bgcolor: '#3311CC', borderRadius: '10px', textTransform: 'none', fontWeight: 700, px: 4, py: 1.2, '&:hover': { bgcolor: '#220899' } }}
              >
                {reportSubmitting ? 'Enregistrement...' : 'Enregistrer le compte rendu'}
              </Button>
            </Stack>
          </Stack>
        </DialogContent>
      </Dialog>
    </Stack>
  );
};

export default ReservationReportsTab;